import { type InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { t } from "../i18n";
import { api, type Schemas, unwrap } from "./client";

// The Inbox (PLAN §8.6; R6 contract 13): what Vantage brought you — relays, reports, reminders
// and alerts — with per-filter counts and cursor paging.

export type InboxItem = Schemas["InboxItemOut"];
export type InboxPage = Schemas["InboxPage"];
export type InboxCounts = Schemas["InboxCounts"];
export type InboxType = "relay" | "report" | "reminder" | "alert";
export type InboxFilter = "all" | "unread" | InboxType;
export type InboxPages = InfiniteData<InboxPage, unknown>;

/** The mockup's filter chips, in its order (each label read in the language now). */
export const INBOX_FILTERS: { id: InboxFilter; readonly label: string }[] = (
  [
    ["all", "inbox.filterAll"],
    ["unread", "inbox.filterUnread"],
    ["relay", "inbox.filterRelay"],
    ["report", "inbox.filterReport"],
    ["reminder", "inbox.filterReminder"],
    ["alert", "inbox.filterAlert"],
  ] as const
).map(([id, key]) => ({
  id,
  get label() {
    return t(key);
  },
}));
export function isInboxFilter(value: unknown): value is InboxFilter {
  return INBOX_FILTERS.some((f) => f.id === value);
}

export const inboxKeys = {
  /** Every inbox query (the sidebar's count, each filter's list): invalidate this prefix. */
  all: ["inbox"] as const,
  summary: ["inbox", "summary"] as const,
  list: (filter: InboxFilter) => ["inbox", "list", filter] as const,
  message: (messageId: string) => ["inbox", "message", messageId] as const,
  item: (itemId: string) => ["inbox", "item", itemId] as const,
};

type InboxQuery = {
  type?: InboxType;
  unread?: boolean;
  before?: string;
  limit: number;
};

/** A filter (and a cursor) → `GET /api/inbox` query. */
export function inboxQuery(filter: InboxFilter, before?: string | null, limit = 30): InboxQuery {
  const q: InboxQuery = { limit };
  if (filter === "unread") q.unread = true;
  else if (filter !== "all") q.type = filter;
  if (before) q.before = before;
  return q;
}

/** A chip's count ("All 5", "Unread 2"); none for an empty type. */
export function chipCount(filter: InboxFilter, counts: InboxCounts | undefined): number {
  if (!counts) return 0;
  return counts[filter] ?? 0;
}

/** The pages' items, newest first, each once (a new item can shift a page boundary). */
export function itemsOf(pages: InboxPages | undefined): InboxItem[] {
  const seen = new Set<string>();
  const out: InboxItem[] = [];
  for (const page of pages?.pages ?? []) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}

/** The newest counts the pages carry (the first page is the latest fetch). */
export function countsOf(pages: InboxPages | undefined): InboxCounts | undefined {
  return pages?.pages[0]?.counts;
}

/** An item read here, before the server says so: its mark goes and the unread count drops.
 *  Under the Unread filter the item stays listed until the next fetch (it's being read). */
export function markReadInPages(
  pages: InboxPages | undefined,
  id: string,
  at: string,
): InboxPages | undefined {
  if (!pages) return pages;
  const wasUnread = pages.pages.some((p) => p.items.some((i) => i.id === id && !i.read_at));
  if (!wasUnread) return pages;
  return {
    ...pages,
    pages: pages.pages.map((p) => ({
      ...p,
      counts: { ...p.counts, unread: Math.max(0, p.counts.unread - 1) },
      items: p.items.map((i) => (i.id === id ? { ...i, read_at: at } : i)),
    })),
  };
}

/** The sidebar's count after reading one item. */
export function markReadInSummary(
  page: InboxPage | undefined,
  wasUnread: boolean,
): InboxPage | undefined {
  if (!page || !wasUnread) return page;
  return { ...page, counts: { ...page.counts, unread: Math.max(0, page.counts.unread - 1) } };
}

/** The rail's Inbox badge (R7.5): only the counts are needed. */
export function useInboxSummary() {
  return useQuery({
    queryKey: inboxKeys.summary,
    queryFn: () => unwrap(api.GET("/api/inbox", { params: { query: { limit: 1 } } })),
    staleTime: 30_000,
  });
}

export function useInboxList(filter: InboxFilter) {
  return useInfiniteQuery({
    queryKey: inboxKeys.list(filter),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/api/inbox", { params: { query: inboxQuery(filter, pageParam) } })),
    getNextPageParam: (last) => last.next_before ?? null,
  });
}

export async function readInboxItem(itemId: string): Promise<void> {
  await unwrap(api.POST("/api/inbox/{item_id}/read", { params: { path: { item_id: itemId } } }));
}

/** One item by id: a link to one that isn't on the loaded pages (R10-N7). */
export async function fetchInboxItem(itemId: string): Promise<InboxItem> {
  return unwrap(api.GET("/api/inbox/{item_id}", { params: { path: { item_id: itemId } } }));
}

/** Every unread item, read (R10-Q7). */
export async function readAllInbox(): Promise<void> {
  await unwrap(api.POST("/api/inbox/read-all", {}));
}

/** One message, where it lives: a thread's reply by its thread, else the top level. */
export async function fetchMessage(
  conversationId: string,
  messageId: string,
  threadRootId?: string | null,
) {
  const page = await unwrap(
    api.GET("/api/conversations/{conversation_id}/messages", {
      params: {
        path: { conversation_id: conversationId },
        query: threadRootId
          ? { thread: threadRootId, around: messageId, limit: 2 }
          : { around: messageId, limit: 2 },
      },
    }),
  );
  return page.messages.find((m) => m.id === messageId) ?? null;
}

/** An item's message (a report's result), for the reading pane. */
export function useInboxMessage(item: InboxItem | null) {
  const conversationId = item?.conversation_id ?? null;
  const messageId = item?.message_id ?? null;
  return useQuery({
    queryKey: inboxKeys.message(messageId ?? ""),
    enabled: Boolean(conversationId && messageId),
    queryFn: () => fetchMessage(conversationId ?? "", messageId ?? "", item?.thread_root_id),
    staleTime: 30_000,
  });
}
